import { SdkContext } from '../internal'
import type {
  AltchaChallenge, Paged, Survey, SurveyAnswerInput, SurveyAudiencePreview, SurveyFilter, SurveyFormView,
  PendingSurveys, SurveyIndividualResponse, SurveyMemberItem, SurveyResults, SurveySpaceConfig, SurveySpaceMode, SurveyStatus, SurveyTemplate,
  SurveyWriteBody, SurveyRound, SurveyEvolution, SurveyHeatmap, SurveyHeatmapDimension,
  MoodPulseLink, MoodPulsePending, MoodTeamTrend, MoodTrend
} from '@/types/surveys'

const listOf = <T,>(result: unknown): Paged<T> => {
  if (Array.isArray(result)) return { items: result as T[] }
  const r = result as Paged<T> | null
  return { items: r?.items || [], pagination: r?.pagination }
}

/**
 * F-103 surveys. Team routes (owner, admin) and the member's form take the
 * community from X-CommunityId; the public link takes it from the token.
 */
export class SurveysResource {
  constructor(private ctx: SdkContext) {}

  /** The ready-made surveys, already in the community's language. */
  async templates(): Promise<SurveyTemplate[]> {
    const result = await this.ctx.call<SurveyTemplate[]>({ httpMethod: 'GET', path: '/surveys/templates' })
    return Array.isArray(result) ? result : []
  }

  /** Owner and admin: every survey (or the space's). */
  async list(params: { spaceId?: string; status?: SurveyStatus; page?: number; limit?: number } = {}): Promise<Paged<Survey>> {
    return listOf<Survey>(await this.ctx.raw({ httpMethod: 'GET', path: '/surveys' }, { query: params }))
  }

  /** Any other role: the opened surveys of a space this person can answer, with `alreadyResponded`. */
  async listForMember(spaceId: string, params: { page?: number; limit?: number } = {}): Promise<Paged<SurveyMemberItem>> {
    return listOf<SurveyMemberItem>(await this.ctx.raw({ httpMethod: 'GET', path: '/surveys' }, { query: { ...params, spaceId } }))
  }

  /** The space's mode, whether this person manages it and, in `single`, its survey. */
  async getSpace(spaceId: string): Promise<SurveySpaceConfig> {
    return this.ctx.call({ httpMethod: 'GET', path: '/surveys/spaces/{spaceId}' }, { spaceId })
  }

  async updateSpace(spaceId: string, mode: SurveySpaceMode): Promise<{ spaceId: string; mode: SurveySpaceMode }> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/surveys/spaces/{spaceId}', hasBody: true }, { spaceId, body: { mode } })
  }

  async get(id: string): Promise<Survey> {
    return this.ctx.call({ httpMethod: 'GET', path: '/surveys/{id}' }, { id })
  }

  async create(body: SurveyWriteBody): Promise<Survey> {
    return this.ctx.call({ httpMethod: 'POST', path: '/surveys', hasBody: true }, { body })
  }

  /** Draft: everything, the tree replaced whole. Open: only title, description, closesAt and the public link. */
  /** `updatedAt`: the version read (409 with `data.updatedAt` when another save came first). */
  async update(id: string, body: SurveyWriteBody & { updatedAt?: string }): Promise<Survey> {
    return this.ctx.call({ httpMethod: 'PATCH', path: '/surveys/{id}', hasBody: true }, { id, body })
  }

  async remove(id: string): Promise<void> {
    await this.ctx.call({ httpMethod: 'DELETE', path: '/surveys/{id}' }, { id })
  }

  /** Opening invites the space by e-mail (F-104 "Pesquisas"); `notifyByEmail: false` opens without it. */
  async open(id: string, notifyByEmail?: boolean): Promise<Survey> {
    return this.ctx.call({ httpMethod: 'POST', path: '/surveys/{id}/open', hasBody: true }, { id, body: notifyByEmail === undefined ? {} : { notifyByEmail } })
  }

  async close(id: string): Promise<Survey> {
    return this.ctx.call({ httpMethod: 'POST', path: '/surveys/{id}/close', hasBody: true }, { id, body: {} })
  }

  /** Who has access to the space (or to the survey's space) today: the people opening invites. */
  async previewAudience(target: { spaceId: string } | { surveyId: string }): Promise<SurveyAudiencePreview> {
    return this.ctx.call({ httpMethod: 'POST', path: '/surveys/audience-preview', hasBody: true }, { body: target })
  }

  async results(id: string, filter: SurveyFilter | null = null): Promise<SurveyResults> {
    return this.ctx.call({ httpMethod: 'GET', path: '/surveys/{id}/results' }, { id, query: filter ? { [filter.key]: filter.value } : {} })
  }

  /** Identified surveys only: who answered what, newest first. */
  async responses(id: string, params: { page?: number; limit?: number } = {}): Promise<Paged<SurveyIndividualResponse>> {
    return listOf<SurveyIndividualResponse>(await this.ctx.raw({ httpMethod: 'GET', path: '/surveys/{id}/responses' }, { id, query: params }))
  }

  /** The CSV as text, with the BOM put back (`fetch` drops it) so spreadsheets read UTF-8. */
  async exportCsv(id: string): Promise<string> {
    const result = await this.ctx.raw<unknown>({ httpMethod: 'GET', path: '/surveys/{id}/export' }, { id })
    const text = typeof result === 'string' ? result : String(result ?? '')
    return text.startsWith('﻿') ? text : '﻿' + text
  }

  /** F-137: recurring survey controls (owner, admin). Pausing keeps the open round; ending deletes no round. */
  async pauseRecurrence(id: string): Promise<Survey> {
    return this.ctx.call({ httpMethod: 'POST', path: '/surveys/{id}/recurrence/pause', hasBody: true }, { id, body: {} })
  }

  async resumeRecurrence(id: string): Promise<Survey> {
    return this.ctx.call({ httpMethod: 'POST', path: '/surveys/{id}/recurrence/resume', hasBody: true }, { id, body: {} })
  }

  async endRecurrence(id: string): Promise<Survey> {
    return this.ctx.call({ httpMethod: 'POST', path: '/surveys/{id}/recurrence/end', hasBody: true }, { id, body: {} })
  }

  /** F-137: the rounds, newest first (`id` is the recurring survey or one of its rounds). */
  async rounds(id: string, params: { page?: number; limit?: number } = {}): Promise<Paged<SurveyRound>> {
    return listOf<SurveyRound>(await this.ctx.raw({ httpMethod: 'GET', path: '/surveys/{id}/rounds' }, { id, query: params }))
  }

  /** F-137: one series per NPS or scale question, one point per opened round. */
  async evolution(id: string): Promise<SurveyEvolution> {
    return this.ctx.call({ httpMethod: 'GET', path: '/surveys/{id}/evolution' }, { id })
  }

  /** F-137: the heat map by one dimension; on a recurring survey `roundId` picks the round. */
  async heatmap(id: string, params: { dimension?: SurveyHeatmapDimension; roundId?: string } = {}): Promise<SurveyHeatmap> {
    return this.ctx.call({ httpMethod: 'GET', path: '/surveys/{id}/heatmap' }, { id, query: params })
  }

  /** F-137: the heat map's CSV, BOM put back like `exportCsv`. */
  async heatmapCsv(id: string, params: { dimension?: SurveyHeatmapDimension; roundId?: string } = {}): Promise<string> {
    const result = await this.ctx.raw<unknown>({ httpMethod: 'GET', path: '/surveys/{id}/heatmap/export' }, { id, query: params })
    const text = typeof result === 'string' ? result : String(result ?? '')
    return text.startsWith('\uFEFF') ? text : '\uFEFF' + text
  }

  /** The member's screen for a survey of the session's community. */
  async form(id: string): Promise<SurveyFormView> {
    return this.ctx.call({ httpMethod: 'GET', path: '/surveys/{id}/form' }, { id })
  }

  async respond(id: string, answers: SurveyAnswerInput[]): Promise<{ respondAs: 'member' }> {
    return this.ctx.call({ httpMethod: 'POST', path: '/surveys/{id}/responses', hasBody: true }, { id, body: { answers } })
  }

  /** Public link, no login needed; with a session of the survey's community it answers as a member. */
  async getPublic(publicToken: string): Promise<SurveyFormView> {
    return this.ctx.call({ httpMethod: 'GET', path: '/public/surveys/{publicToken}' }, { publicToken })
  }

  /** A signed ALTCHA challenge for a visitor's submission (5 minutes, this survey only). */
  async getPublicChallenge(publicToken: string): Promise<AltchaChallenge> {
    return this.ctx.call({ httpMethod: 'GET', path: '/public/surveys/{publicToken}/challenge' }, { publicToken })
  }

  async respondPublic(
    publicToken: string,
    body: { answers: SurveyAnswerInput[]; visitorToken?: string; altcha?: string }
  ): Promise<{ respondAs: 'member' | 'visitor' }> {
    return this.ctx.call({ httpMethod: 'POST', path: '/public/surveys/{publicToken}/responses', hasBody: true }, { publicToken, body })
  }

  /** F-103 1b: the caller's own pending surveys (never someone else's). */
  async pending(): Promise<PendingSurveys> {
    const r = await this.ctx.call<PendingSurveys | null>({ httpMethod: 'GET', path: '/surveys/pending' })
    return { notice: Boolean(r?.notice), blocking: Boolean(r?.blocking), surveys: r && Array.isArray(r.surveys) ? r.surveys : [] }
  }

  /** Closes the member's notice of one survey; it does not come back. */
  async dismissNotice(id: string): Promise<void> {
    await this.ctx.call({ httpMethod: 'POST', path: '/surveys/{id}/notice/dismiss', hasBody: true }, { id, body: {} })
  }

  /** F-241: the open mood pulse round waiting for this person (the Home card), or null. */
  async moodPulsePending(): Promise<MoodPulsePending | null> {
    const r = await this.ctx.call<MoodPulsePending | null>({ httpMethod: 'GET', path: '/surveys/mood-pulse/pending' })
    return r && r.id ? r : null
  }

  /** F-241: which round a face of the e-mail opens. The face itself never goes to the server. */
  async moodPulseLink(token: string): Promise<MoodPulseLink> {
    return this.ctx.call({ httpMethod: 'GET', path: '/surveys/mood-pulse/link/{token}' }, { token })
  }

  /** F-241, owner and admin: the weekly mood of the community and of each group. */
  async moodTrend(id: string): Promise<MoodTrend> {
    return this.ctx.call({ httpMethod: 'GET', path: '/surveys/{id}/mood-trend' }, { id })
  }

  /** F-241, whoever leads someone: the team's weekly mood (403 `moodPulseNoTeam` otherwise). */
  async moodPulseMyTeam(): Promise<MoodTeamTrend> {
    return this.ctx.call({ httpMethod: 'GET', path: '/surveys/mood-pulse/my-team' })
  }
}
